import { cn } from "@newxsystems/shared";
import { resolveKaajPrimitive } from "../../../../client";
import { getKaajSectionSpacing } from "../../../../modules/layout/kaaj-section-spacing";
import { KaajAction } from "../_shared/KaajAction";
import type {
  BlockLink,
  BlockMedia,
  IFeaturedTestimonialBlockProps,
} from "./FeaturedTestimonialBlock.props";
import { RichTextHtml } from "../../RichTextHtml";

const headerAlignmentClasses = {
  center: "mx-auto items-center text-center",
  left: "items-start text-left",
  right: "ml-auto items-end text-right",
} satisfies Record<"left" | "center" | "right", string>;

const actionAlignmentClasses = {
  center: "justify-center",
  left: "justify-start",
  right: "justify-end",
} satisfies Record<"left" | "center" | "right", string>;

const Rating = ({ value }: { value?: number | null }) => {
  const rating = value;

  if (rating == null) return null;

  return (
    <div
      aria-label={`${rating} out of 5 star rating`}
      className="flex items-center gap-1.5"
      role="img"
    >
      {Array.from({ length: 5 }).map((_, index) => (
        <span
          aria-hidden="true"
          className={cn(
            "text-[1.05rem] leading-none drop-shadow-[0_3px_8px_rgba(245,184,75,0.22)]",
            index < rating ? "text-[#E9A72E]" : "text-[#CBD6FF]",
          )}
          key={index}
        >
          ★
        </span>
      ))}
    </div>
  );
};

const getSafeRel = (link?: BlockLink | null) => {
  const href = link?.href;
  const external = href?.startsWith("http") || href?.startsWith("//");

  return link?.target === "_blank" || external ? "noopener noreferrer" : undefined;
};

const getImageAlt = (image?: BlockMedia | null) => image?.alternateText ?? "";

export const FeaturedTestimonialBlock = (props: IFeaturedTestimonialBlockProps) => {
  const Image = resolveKaajPrimitive("Image");
  const Link = resolveKaajPrimitive("Link");
  const { parentClass, spacing, testimonial } = props;
  const resolvedAlignment: "left" | "center" | "right" = "center";
  const eyebrow = props.eyebrow;
  const heading = props.heading;
  const subheading = props.subheading;
  const quote = testimonial?.quote;
  const personName = testimonial?.personName;
  const role = testimonial?.role;
  const company = testimonial?.company;
  const rating = testimonial?.rating;
  const avatar = testimonial?.avatar;
  const avatarSrc = avatar?.defaultSrc;
  const testimonialLink = testimonial?.link;
  const actions = props.buttons ?? [];
  const sectionId = props.anchorId;
  const headingId = sectionId && heading ? `${sectionId}-heading` : undefined;
  const identityMeta = [role, company].filter(Boolean).join(" · ");
  const hasIntro = Boolean(eyebrow || heading || subheading);
  const hasActions = actions.length > 0;
  const hasIdentity = Boolean(personName || identityMeta || avatarSrc);

  if (!quote && !hasIntro) {
    return null;
  }

  return (
    <section
      className={cn(
        "relative isolate overflow-hidden text-[#151927]",
        getKaajSectionSpacing(spacing),
        parentClass,
      )}
      data-kaaj-block="featuredTestimonial"
      id={sectionId ?? undefined}
      {...(headingId ? { "aria-labelledby": headingId } : {})}
    >
      <div className="kaaj-container relative">
        {hasIntro ? (
          <div
            className={cn(
              "mb-10 flex max-w-4xl flex-col gap-0 lg:mb-14",
              headerAlignmentClasses[resolvedAlignment],
            )}
          >
            {eyebrow ? (
              <p className="inline-flex max-w-full rounded-full border border-[#CAD7FF] bg-white px-4 py-1.5 text-xs font-extrabold uppercase tracking-[0.18em] text-[#2144FF] shadow-[0_10px_26px_rgba(33,68,255,0.09)] [overflow-wrap:anywhere]">
                {eyebrow}
              </p>
            ) : null}
            {heading ? (
              <h2 className="kaaj-section-heading mt-4 max-w-4xl text-[#151927]" id={headingId}>
                {heading}
              </h2>
            ) : null}
            {subheading ? (
              <RichTextHtml
                className={cn(
                  "mt-5 max-w-2xl text-pretty text-base leading-7 text-[#4B5568] [overflow-wrap:anywhere] sm:text-lg sm:leading-8",
                  resolvedAlignment === "center" && "mx-auto",
                )}
                html={subheading}
              />
            ) : null}
            {hasActions ? (
              <div
                className={cn(
                  "mt-7 flex w-full flex-wrap gap-3 max-sm:flex-col max-sm:items-stretch",
                  actionAlignmentClasses[resolvedAlignment],
                )}
              >
                {actions.map((action, index) => (
                  <KaajAction
                    ariaLabel={action.ariaLabel}
                    className="max-sm:w-full"
                    href={action.href}
                    key={`${action.href}-${action.text}-${index}`}
                    label={action.text}
                    target={action.target}
                    variant={action.style}
                  />
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        {quote ? (
          <div className="relative overflow-hidden rounded-[2rem] border border-[#D9E1FF] bg-white p-5 shadow-[0_24px_70px_rgba(33,68,255,0.10)] sm:p-7 lg:rounded-[2.25rem] lg:p-9 xl:p-10">
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-[#8FA3FF] to-transparent"
            />
            <span
              aria-hidden="true"
              className="pointer-events-none absolute -right-16 top-10 h-44 w-44 rounded-full bg-[#FFEAF9]/74 blur-3xl"
            />
            <span
              aria-hidden="true"
              className="pointer-events-none absolute -bottom-20 left-12 h-44 w-44 rounded-full bg-[#D9E1FF]/62 blur-3xl"
            />

            <div className="relative grid min-w-0 gap-8 lg:grid-cols-[minmax(0,0.92fr)_minmax(17rem,0.38fr)] lg:items-start lg:gap-9 xl:gap-11">
              <figure className="min-w-0 lg:max-w-[50rem]">
                {rating == null ? null : (
                  <div className="flex flex-wrap items-center gap-4 sm:justify-end">
                    <Rating value={rating} />
                  </div>
                )}

                {quote ? (
                  <blockquote className="">
                    <span className="grid size-12 place-items-center rounded-[1.1rem] border border-[#CAD7FF] bg-[#EEF2FF] text-4xl font-extrabold leading-none text-[#2144FF] shadow-[0_12px_28px_rgba(33,68,255,0.12)]">
                      “
                    </span>
                    <p className="mt-6 max-w-[30ch] text-pretty text-[1.55rem] lg:text-[2.7rem] font-extrabold leading-[1.08] tracking-[-0.04em] text-[#151927] [overflow-wrap:anywhere]">
                      {quote}
                    </p>
                  </blockquote>
                ) : null}

                {(testimonialLink?.href || (hasActions && !hasIntro)) && (
                  <figcaption className="mt-8 grid gap-5 border-t border-[#E6EBFF] pt-6 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
                    {testimonialLink?.href && (
                      <div className="min-w-0 text-sm leading-6">
                        {testimonialLink?.href && testimonialLink.text ? (
                          <Link
                            aria-label={testimonialLink.ariaLabel ?? undefined}
                            className="inline-flex max-w-full items-center rounded-full text-sm font-extrabold text-[#2144FF] underline decoration-[#2144FF]/25 underline-offset-4 transition-[color,decoration-color,transform] duration-200 hover:text-[#132EDB] hover:decoration-[#132EDB] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#2144FF] motion-reduce:transition-none motion-safe:hover:-translate-y-0.5"
                            href={testimonialLink.href}
                            rel={getSafeRel(testimonialLink)}
                            target={testimonialLink.target ?? undefined}
                            title={testimonialLink.ariaLabel ?? undefined}
                          >
                            <span className="min-w-0 [overflow-wrap:anywhere]">
                              {testimonialLink.text}
                            </span>
                            <span aria-hidden="true" className="ml-2 shrink-0">
                              →
                            </span>
                          </Link>
                        ) : null}
                      </div>
                    )}

                    {hasActions && !hasIntro ? (
                      <div className="flex flex-wrap gap-3 max-sm:flex-col max-sm:items-stretch sm:justify-end">
                        {actions.map((action, index) => (
                          <KaajAction
                            ariaLabel={action.ariaLabel}
                            className="max-sm:w-full"
                            href={action.href}
                            key={`${action.href}-${action.text}-${index}`}
                            label={action.text}
                            target={action.target}
                            variant={action.style}
                          />
                        ))}
                      </div>
                    ) : null}
                  </figcaption>
                )}
              </figure>

              {hasIdentity ? (
                <aside className="relative min-w-0 self-start overflow-hidden rounded-[1.75rem] border border-[#CAD7FF] bg-[radial-gradient(circle_at_85%_14%,rgba(255,234,249,0.62),transparent_34%),linear-gradient(180deg,#F8FAFF_0%,#FFFFFF_100%)] p-5 shadow-[0_18px_46px_rgba(33,68,255,0.09)] lg:self-center lg:p-6 xl:p-7">
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-[#8FA3FF] to-transparent"
                  />

                  <div className="flex items-center gap-4">
                    {avatarSrc ? (
                      <div className="relative grid size-16 shrink-0 place-items-center overflow-hidden rounded-full border border-[#CAD7FF] bg-[radial-gradient(circle_at_28%_18%,#FFEAF9,transparent_34%),linear-gradient(135deg,#EEF2FF,#D9E1FF)] p-1 shadow-[0_12px_30px_rgba(33,68,255,0.12)]">
                        <Image
                          alt={getImageAlt(avatar)}
                          aspectRatio="square"
                          className="h-full w-full rounded-full"
                          height={avatar?.height ?? undefined}
                          imageClassName="h-full w-full rounded-full object-cover"
                          loading="lazy"
                          src={avatarSrc}
                          srcSet={avatar?.srcSet ?? undefined}
                          style={
                            avatar?.focalPoint
                              ? {
                                  objectPosition: `${avatar.focalPoint.x}% ${avatar.focalPoint.y}%`,
                                }
                              : undefined
                          }
                          width={avatar?.width ?? undefined}
                        />
                      </div>
                    ) : null}
                    <div className="min-w-0">
                      {personName ? (
                        <p className="text-base font-extrabold leading-6 text-[#151927] [overflow-wrap:anywhere]">
                          {personName}
                        </p>
                      ) : null}
                      {identityMeta ? (
                        <p className="mt-1 text-sm font-medium leading-6 text-[#4B5568] [overflow-wrap:anywhere]">
                          {identityMeta}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </aside>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
};

FeaturedTestimonialBlock.displayName = "Block::FeaturedTestimonialBlock";
